import React, { useCallback, useEffect, useState } from 'react';
import { req } from '../services/api';

const money = value => Number(value || 0).toFixed(2) + ' €';

const formatDate = value => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('cs-CZ');
};

const roleLabel = role => {
  const labels = {
    CUSTOMER: 'Zákazník',
    DRIVER: 'Řidič',
    FLEET: 'Flotila',
    FLEET_DRIVER: 'Řidič flotily'
  };

  return labels[role] || role || '—';
};

export default function Referrals() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const loadReferrals = useCallback(async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '25'
      });

      if (search.trim()) {
        params.set('search', search.trim());
      }

      if (role) {
        params.set('role', role);
      }

      const data = await req('GET', '/referrals?' + params.toString());

      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      setTotalPages(Number(data?.totalPages || 1));
    } catch (error) {
      console.error('Referrals load failed:', error);
      setItems([]);
      setTotal(0);
      setTotalPages(1);

      alert(
        'Chyba při načítání referral programu: ' +
          (error?.message || 'neznámá chyba')
      );
    } finally {
      setLoading(false);
    }
  }, [page, search, role]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  useEffect(() => {
    setPage(1);
  }, [search, role]);

  const referredCustomers = items.reduce(
    (sum, item) => sum + Number(item.stats?.referredCustomers || 0),
    0
  );

  const rewardedRides = items.reduce(
    (sum, item) => sum + Number(item.stats?.rewardedRides || 0),
    0
  );

  const totalRewards = items.reduce(
    (sum, item) => sum + Number(item.stats?.totalRewardAmount || 0),
    0
  );

  return (
    <div>
      <div className="page-title">Referral program</div>
      <div className="page-sub">
        Přehled doporučitelů, přivedených zákazníků a 2% odměn
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{total}</div>
          <div className="stat-label">Aktivní doporučitelé</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{referredCustomers}</div>
          <div className="stat-label">Doporučení zákazníci</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{rewardedRides}</div>
          <div className="stat-label">Odměněné jízdy</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{money(totalRewards)}</div>
          <div className="stat-label">Referral odměny</div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 14
        }}
      >
        <input
          className="search-bar"
          style={{ marginBottom: 0 }}
          placeholder="Hledat podle jména, e-mailu nebo referral kódu..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          style={{ minWidth: 170 }}
        >
          <option value="">Všechny role</option>
          <option value="CUSTOMER">Zákazník</option>
          <option value="DRIVER">Řidič</option>
          <option value="FLEET">Flotila</option>
          <option value="FLEET_DRIVER">Řidič flotily</option>
        </select>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Referral program</span>
          <span style={{ color: 'var(--gray)', fontSize: 12 }}>
            Celkem {total}
          </span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Doporučitel</th>
                <th>Role</th>
                <th>Referral kód</th>
                <th>Zákazníci</th>
                <th>Odměněné jízdy</th>
                <th>Odměny</th>
                <th>Akce</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="loading-cell">
                    Načítám...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-cell">
                    Žádná referral data
                  </td>
                </tr>
              ) : (
                items.map(item => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <strong>{item.name || '—'}</strong>
                        <br />
                        <span
                          style={{
                            color: 'var(--gray)',
                            fontSize: 11
                          }}
                        >
                          {item.email || '—'}
                        </span>
                      </td>

                      <td>
                        <span className="badge badge-gray">
                          {roleLabel(item.role)}
                        </span>
                      </td>

                      <td>
                        <strong style={{ letterSpacing: 1 }}>
                          {item.referralCode || '—'}
                        </strong>
                      </td>

                      <td>
                        <strong>
                          {item.stats?.referredCustomers || 0}
                        </strong>
                      </td>

                      <td>
                        {item.stats?.rewardedRides || 0}
                      </td>

                      <td
                        style={{
                          color: 'var(--gold)',
                          fontWeight: 700
                        }}
                      >
                        {money(item.stats?.totalRewardAmount)}
                      </td>

                      <td>
                        <button
                          className="btn-sm btn-blue"
                          onClick={() =>
                            setExpandedId(
                              expandedId === item.id ? null : item.id
                            )
                          }
                        >
                          {expandedId === item.id
                            ? 'Skrýt'
                            : 'Detail'}
                        </button>
                      </td>
                    </tr>

                    {expandedId === item.id && (
                      <tr>
                        <td colSpan="7">
                          <div style={{ padding: '8px 4px 14px' }}>
                            <strong>
                              Doporučení zákazníci
                            </strong>

                            {item.referredCustomers?.length ? (
                              <div
                                className="table-wrap"
                                style={{ marginTop: 10 }}
                              >
                                <table>
                                  <thead>
                                    <tr>
                                      <th>Zákazník</th>
                                      <th>Registrace</th>
                                      <th>Odměněné jízdy</th>
                                      <th>Vygenerovaná odměna</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {item.referredCustomers.map(customer => (
                                      <tr key={customer.id}>
                                        <td>
                                          <strong>
                                            {customer.name || '—'}
                                          </strong>
                                          <br />
                                          <span
                                            style={{
                                              color: 'var(--gray)',
                                              fontSize: 11
                                            }}
                                          >
                                            {customer.email || '—'}
                                          </span>
                                        </td>

                                        <td>
                                          {formatDate(customer.createdAt)}
                                        </td>

                                        <td>
                                          {customer.rewardedRides || 0}
                                        </td>

                                        <td>
                                          {money(
                                            customer.totalRewardAmount
                                          )}
                                        </td>

                                        <td>
                                          {customer.deletedAt ? (
                                            <span className="badge badge-red">
                                              Odstraněn
                                            </span>
                                          ) : (
                                            <span className="badge badge-green">
                                              Aktivní
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div
                                style={{
                                  marginTop: 10,
                                  color: 'var(--gray)'
                                }}
                              >
                                Žádní doporučení zákazníci.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
            marginTop: 16
          }}
        >
          <button
            className="btn-sm btn-gray"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Předchozí
          </button>

          <span style={{ fontSize: 12 }}>
            Strana {page} z {totalPages}
          </span>

          <button
            className="btn-sm btn-gray"
            disabled={page >= totalPages}
            onClick={() =>
              setPage(p => Math.min(totalPages, p + 1))
            }
          >
            Další
          </button>
        </div>
      )}
    </div>
  );
}